import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../services/api.service';

@Component({
  selector: 'app-team-list',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './team-list.component.html',
  styleUrls: ['./team-list.component.css']
})
export class TeamListComponent implements OnInit {
  teamMembers: any[] = [];
  filteredTeamMembers: any[] = [];
  managers: any[] = [];
  loading = false;
  showAddForm = false;
  showEditModal = false;
  showResetPasswordModalFlag = false;
  showSuccessModal = false;
  successModalMessage = '';
  editingMember: any = null;
  resetPasswordMember: any = null;
  resetPasswordForm = {
    password: ''
  };
  passwordError = '';
  resetPasswordNotification = '';
  resetPasswordNotificationType: 'success' | 'error' | null = null;
  editUserNotification = '';
  editUserNotificationType: 'success' | 'error' | null = null;
  // Field validation errors for Add User form
  firstNameError = '';
  emailError = '';
  passwordErrorAdd = '';
  phoneError = '';
  addUserError = ''; // Error specific to add user form
  successMessage = ''; // Success notification message
  errorMessage = ''; // Error notification message
  userForm = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    phoneCountryCode: 'IN +91',
    role: 'ProcurementManager',
    country: 'India'
  };
  editForm = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    phoneCountryCode: 'IN +91',
    role: 'ProcurementManager',
    country: 'India'
  };
  countryCodes = [
    { code: 'IN +91', label: 'IN +91' },
    { code: 'US +1', label: 'US +1' },
    { code: 'UK +44', label: 'UK +44' },
    { code: 'CN +86', label: 'CN +86' }
  ];
  roles = [
    { value: 'ProcurementManager', label: 'Procurement Manager' }
  ];
  formLoading = false;
  error = '';
  success = '';
  showPassword = false;
  
  // Search and filters
  searchTerm = '';
  selectedCountry = '';
  selectedStatus = '';
  countries: string[] = ['India', 'USA', 'UK', 'China'];
  showActionMenu: { [key: string]: boolean } = {};
  hasMoreData = false; // Track if there's more data to load (set to false since all data loads at once)

  constructor(private apiService: ApiService) {}

  ngOnInit() {
    this.loadTeamMembers();
  }

  loadTeamMembers() {
    this.loading = true;
    this.error = '';
    this.apiService.getUsers('ProcurementManager').subscribe({
      next: (data) => {
        console.log('Team members loaded:', data);
        this.managers = data;
        this.teamMembers = data.map((member: any, index: number) => {
          // Use managerId from database if available, otherwise fallback to generated ID
          let userId = member.managerId;
          if (!userId) {
            userId = member._id ? `U${member._id.slice(-6)}` : `U${String(index + 1).padStart(6, '0')}`;
          }
          return {
            ...member,
            managerId: member.managerId || userId, // Store managerId for display
            userId: userId, // Keep userId for backward compatibility
            role: member.userType === 'ProcurementManager' ? 'Procurement Manager' : member.userType || 'Procurement Manager',
            country: member.country || 'India',
            createdAt: member.createdAt || new Date(),
            updatedAt: member.updatedAt || new Date(),
            isActive: member.isActive !== undefined ? member.isActive : true
          };
        });
        this.filteredTeamMembers = [...this.teamMembers];
        this.hasMoreData = false; // All data loaded, no more to load
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading team members:', err);
        if (err.status === 0) {
          this.error = 'Cannot connect to server. Please make sure the backend API is running.';
        } else if (err.status === 401) {
          this.error = 'Unauthorized. Please login again.';
        } else if (err.status === 403) {
          this.error = 'Access denied. Only Admin can view team members.';
        } else {
          this.error = err.error?.message || 'Error loading team members. Please try again.';
        }
        this.loading = false;
      }
    });
  }

  onSearch() {
    this.applyFilters();
  }

  applyFilters() {
    this.filteredTeamMembers = this.teamMembers.filter(member => {
      const searchLower = this.searchTerm?.toLowerCase() || '';
      const matchesSearch = !this.searchTerm || 
        member.name?.toLowerCase().includes(searchLower) ||
        member.email?.toLowerCase().includes(searchLower) ||
        member.managerId?.toLowerCase().includes(searchLower);
      
      const matchesCountry = !this.selectedCountry || member.country === this.selectedCountry;
      
      const matchesStatus = !this.selectedStatus || 
        (this.selectedStatus === 'Active' && member.isActive) ||
        (this.selectedStatus === 'Inactive' && !member.isActive);

      return matchesSearch && matchesCountry && matchesStatus;
    });
  }

  resetFilters() {
    this.searchTerm = '';
    this.selectedCountry = '';
    this.selectedStatus = '';
    this.applyFilters();
  }

  toggleActionMenu(userId: string, event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    // Close all other menus
    Object.keys(this.showActionMenu).forEach(key => {
      if (key !== userId) {
        this.showActionMenu[key] = false;
      }
    });
    this.showActionMenu[userId] = !this.showActionMenu[userId];
  }

  closeActionMenu() {
    Object.keys(this.showActionMenu).forEach(key => {
      this.showActionMenu[key] = false;
    });
  }

  activateMember(member: any) {
    this.apiService.updateUserStatus(member._id, true).subscribe({
      next: (response) => {
        console.log('Member activated:', response);
        this.successModalMessage = 'User has been activated';
        this.showSuccessModal = true;
        this.loadTeamMembers();
        this.closeActionMenu();
        setTimeout(() => {
          this.closeSuccessModal();
        }, 3000);
      },
      error: (err) => {
        console.error('Error activating member:', err);
        this.error = err.error?.message || 'Error activating member. Please try again.';
        setTimeout(() => {
          this.error = '';
        }, 3000);
      }
    });
  }

  deactivateMember(member: any) {
    this.apiService.updateUserStatus(member._id, false).subscribe({
      next: (response) => {
        console.log('Member deactivated:', response);
        this.successModalMessage = 'User has been deactivated';
        this.showSuccessModal = true;
        this.loadTeamMembers();
        this.closeActionMenu();
        setTimeout(() => {
          this.closeSuccessModal();
        }, 3000);
      },
      error: (err) => {
        console.error('Error deactivating member:', err);
        this.error = err.error?.message || 'Error deactivating member. Please try again.';
        setTimeout(() => {
          this.error = '';
        }, 3000);
      }
    });
  }

  formatDate(date: any): string {
    if (!date) return '-';
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }

  viewMember(member: any) {
    console.log('View team member:', member);
    // Implement view functionality
  }

  showAddUserForm() {
    this.showAddForm = true;
    this.resetForm();
  }

  resetForm() {
    this.userForm = {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      phone: '',
      phoneCountryCode: 'IN +91',
      role: 'ProcurementManager',
      country: 'India'
    };
    this.firstNameError = '';
    this.emailError = '';
    this.passwordErrorAdd = '';
    this.phoneError = '';
    this.addUserError = '';
    this.successMessage = '';
    this.errorMessage = '';
  }

  showSuccessNotification(message: string) {
    this.successMessage = message;
    setTimeout(() => {
      this.successMessage = '';
    }, 5000);
  }

  showErrorNotification(message: string) {
    this.errorMessage = message;
    setTimeout(() => {
      this.errorMessage = '';
    }, 5000);
  }

  createUser() {
    // Clear all field errors
    this.firstNameError = '';
    this.emailError = '';
    this.passwordErrorAdd = '';
    this.phoneError = '';
    this.addUserError = '';
    this.successMessage = '';
    this.errorMessage = '';

    let hasErrors = false;
    let validationErrors: string[] = [];

    // Validate First Name
    if (!this.userForm.firstName || !this.userForm.firstName.trim()) {
      this.firstNameError = 'First Name is required';
      validationErrors.push('First Name is required');
      hasErrors = true;
    }

    // Validate Email
    if (!this.userForm.email || !this.userForm.email.trim()) {
      this.emailError = 'Email ID is required';
      validationErrors.push('Email ID is required');
      hasErrors = true;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.userForm.email.trim())) {
        this.emailError = 'Please enter a valid email address';
        validationErrors.push('Please enter a valid email address');
        hasErrors = true;
      }
    }

    // Validate Password
    if (!this.userForm.password || !this.userForm.password.trim()) {
      this.passwordErrorAdd = 'Password is required';
      validationErrors.push('Password is required');
      hasErrors = true;
    } else if (this.userForm.password.length < 6) {
      this.passwordErrorAdd = 'Password must be at least 6 characters long';
      validationErrors.push('Password must be at least 6 characters long');
      hasErrors = true;
    }

    // Validate Phone
    if (!this.userForm.phone || !this.userForm.phone.trim()) {
      this.phoneError = 'Phone No is required';
      validationErrors.push('Phone No is required');
      hasErrors = true;
    }

    if (hasErrors) {
      // Show validation errors in notifier
      const errorMsg = validationErrors.join(', ');
      this.showErrorNotification(errorMsg);
      return;
    }

    this.formLoading = true;
    this.addUserError = '';

    // Combine first name and last name
    const fullName = this.userForm.lastName 
      ? `${this.userForm.firstName.trim()} ${this.userForm.lastName.trim()}`.trim()
      : this.userForm.firstName.trim();

    // Combine country code and phone
    const phoneNumber = this.userForm.phoneCountryCode 
      ? `${this.userForm.phoneCountryCode} ${this.userForm.phone.trim()}`.trim()
      : this.userForm.phone.trim();

    const userData = {
      name: fullName,
      email: this.userForm.email.trim().toLowerCase(),
      password: this.userForm.password,
      phone: phoneNumber,
      country: this.userForm.country || 'India',
      userType: this.userForm.role || 'ProcurementManager'
    };

    console.log('Creating user:', { ...userData, password: '***' });

    this.apiService.createUser(userData).subscribe({
      next: (response: any) => {
        console.log('User created successfully:', response);
        this.formLoading = false;
        const successMsg = response.message || 'User created successfully!';
        this.showSuccessNotification(successMsg);
        this.loadTeamMembers();
        setTimeout(() => {
          this.showAddForm = false;
          this.resetForm();
        }, 2000);
      },
      error: (err: any) => {
        console.error('Error creating user:', err);
        this.formLoading = false;
        
        // Determine error message
        let errorMsg = '';
        if (err.status === 0) {
          errorMsg = 'Cannot connect to server. Please make sure the backend API is running.';
        } else if (err.status === 400) {
          errorMsg = err.error?.message || 'Invalid data. Please check your input.';
        } else if (err.status === 401) {
          errorMsg = 'Unauthorized. Please login again.';
        } else if (err.status === 403) {
          errorMsg = 'Access denied. Only Admin can create users.';
        } else {
          errorMsg = err.error?.message || err.message || 'Error creating user. Please try again.';
        }
        
        // Check for duplicate email error - show in field AND notifier
        if (err.error?.message && (err.error.message.includes('email') || err.error.message.includes('Email') || err.error.message.includes('already exists'))) {
          const emailErrorMsg = 'User with this email already exists';
          this.emailError = emailErrorMsg;
          this.showErrorNotification(emailErrorMsg);
        } else {
          // Show general error in form AND notifier
          this.addUserError = errorMsg;
          this.showErrorNotification(errorMsg);
        }
      }
    });
  }

  onModalOverlayClick(event: Event) {
    // Only close modal if clicking directly on overlay, not on modal content
    const target = event.target as HTMLElement;
    const currentTarget = event.currentTarget as HTMLElement;
    
    // Find the modal-content element
    const modalContent = currentTarget.querySelector('.modal-content');
    
    // Check if the click is on the overlay itself (not on modal-content or its children)
    // If modal-content exists and contains the target, don't close
    if (modalContent && modalContent.contains(target)) {
      return;
    }
    
    // If click is on overlay itself, close the modal
    if (target === currentTarget || target.classList.contains('modal-overlay')) {
      this.cancelForm();
    }
  }

  onEditModalOverlayClick(event: Event) {
    // Only close modal if clicking directly on overlay, not on modal content
    const target = event.target as HTMLElement;
    const currentTarget = event.currentTarget as HTMLElement;
    
    // Find the modal-content element
    const modalContent = currentTarget.querySelector('.modal-content');
    
    // Check if the click is on the overlay itself (not on modal-content or its children)
    // If modal-content exists and contains the target, don't close
    if (modalContent && modalContent.contains(target)) {
      return;
    }
    
    // If click is on overlay itself, close the modal
    if (target === currentTarget || target.classList.contains('modal-overlay')) {
      this.closeEditModal();
    }
  }

  cancelForm() {
    this.showAddForm = false;
    this.resetForm();
  }

  editMember(member: any) {
    this.editingMember = member;
    // Split name into first and last name
    const nameParts = (member.name || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    // Extract country code from phone if present
    let phoneNumber = member.phone || '';
    let phoneCountryCode = 'IN +91';
    if (phoneNumber && phoneNumber.includes('+')) {
      // Try to match format like "IN +91 1234567890" or "+91 1234567890"
      const match = phoneNumber.match(/^([A-Z]{2}\s*\+\d+|\+\d+)\s+(.+)$/);
      if (match) {
        phoneCountryCode = match[1].trim();
        phoneNumber = match[2].trim();
      } else {
        // Fallback: split by first space
        const spaceIndex = phoneNumber.indexOf(' ');
        if (spaceIndex > 0) {
          phoneCountryCode = phoneNumber.substring(0, spaceIndex).trim();
          phoneNumber = phoneNumber.substring(spaceIndex + 1).trim();
        }
      }
    }
    
    this.editForm = {
      firstName: firstName,
      lastName: lastName,
      email: member.email || '',
      password: '', // Don't pre-fill password
      phone: phoneNumber,
      phoneCountryCode: phoneCountryCode,
      role: member.userType || 'ProcurementManager',
      country: member.country || 'India'
    };
    this.showEditModal = true;
    this.error = '';
    this.success = '';
    this.editUserNotification = '';
    this.editUserNotificationType = null;
    this.closeActionMenu();
  }

  closeEditModal() {
    this.showEditModal = false;
    this.editUserNotification = '';
    this.editUserNotificationType = null;
    this.editingMember = null;
    this.editForm = {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      phone: '',
      phoneCountryCode: 'IN +91',
      role: 'ProcurementManager',
      country: 'India'
    };
    this.error = '';
    this.success = '';
  }

  updateMember() {
    if (!this.editForm.firstName || !this.editForm.email || !this.editForm.phone) {
      this.showErrorNotification('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.editForm.email)) {
      this.showErrorNotification('Please enter a valid email address');
      return;
    }

    this.formLoading = true;
    this.successMessage = '';
    this.errorMessage = '';

    // Combine first name and last name
    const fullName = this.editForm.lastName 
      ? `${this.editForm.firstName.trim()} ${this.editForm.lastName.trim()}`.trim()
      : this.editForm.firstName.trim();

    // Combine country code and phone
    const phoneNumber = this.editForm.phoneCountryCode 
      ? `${this.editForm.phoneCountryCode} ${this.editForm.phone?.trim()}`.trim()
      : this.editForm.phone.trim();

    const updateData: any = {
      name: fullName,
      email: this.editForm.email.trim().toLowerCase(),
      phone: phoneNumber,
      country: this.editForm.country || 'India',
      userType: 'ProcurementManager'
    };

    console.log('Updating team member:', { ...updateData, password: updateData.password ? '***' : 'not changed' });

    this.apiService.updateUser(this.editingMember._id, updateData).subscribe({
      next: (response: any) => {
        console.log('Team member updated successfully:', response);
        this.formLoading = false;
        
        // Close modal immediately on success
        this.showEditModal = false;
        this.editingMember = null;
        this.loadTeamMembers();
        
        // Show success notification
        this.showSuccessNotification(response.message || 'User updated successfully!');
      },
      error: (err: any) => {
        console.error('Error updating team member:', err);
        this.formLoading = false;
        
        // Determine error message
        let errorMessage = '';
        if (err.status === 0) {
          errorMessage = 'Cannot connect to server. Please make sure the backend API is running.';
        } else if (err.status === 401) {
          errorMessage = 'Unauthorized. Please login again.';
        } else if (err.status === 403) {
          errorMessage = 'Access denied. Only Admin can update team members.';
        } else if (err.status === 400) {
          errorMessage = err.error?.message || 'Invalid data. Please check your input.';
        } else {
          errorMessage = err.error?.message || err.message || 'Error updating team member. Please try again.';
        }
        
        // Show error notification
        this.showErrorNotification(errorMessage);
      }
    });
  }

  showResetPasswordModal(member: any) {
    this.resetPasswordMember = member;
    this.resetPasswordForm = {
      password: ''
    };
    this.showResetPasswordModalFlag = true;
    this.error = '';
    this.success = '';
    this.passwordError = '';
    this.resetPasswordNotification = '';
    this.resetPasswordNotificationType = null;
    this.closeActionMenu();
  }

  closeResetPasswordModal() {
    this.showResetPasswordModalFlag = false;
    this.resetPasswordMember = null;
    this.resetPasswordForm = {
      password: ''
    };
    this.error = '';
    this.success = '';
    this.passwordError = '';
    this.resetPasswordNotification = '';
    this.resetPasswordNotificationType = null;
  }

  onPasswordInput() {
    // Clear error when user starts typing
    if (this.passwordError) {
      this.passwordError = '';
    }
  }

  resetPassword() {
    // Clear all errors
    this.error = '';
    this.passwordError = '';

    // Validate password
    if (!this.resetPasswordForm.password) {
      this.passwordError = 'Password is required';
      return;
    }

    if (this.resetPasswordForm.password.length < 6) {
      this.passwordError = 'Password must be at least 6 characters long';
      return;
    }

    this.formLoading = true;
    this.error = '';
    this.success = '';
    this.passwordError = '';

    const updateData = {
      password: this.resetPasswordForm.password
    };

    this.apiService.updateUser(this.resetPasswordMember._id, updateData).subscribe({
      next: (response: any) => {
        console.log('Password reset successfully:', response);
        this.formLoading = false;
        
        // Clear all errors
        this.error = '';
        this.passwordError = '';
        
        // Show success notification
        this.resetPasswordNotification = 'Password reset successfully!';
        this.resetPasswordNotificationType = 'success';
        
        // Close modal after showing success
        setTimeout(() => {
          this.closeResetPasswordModal();
        }, 500);
        
        // Clear notification after 3 seconds
        setTimeout(() => {
          this.resetPasswordNotification = '';
          this.resetPasswordNotificationType = null;
        }, 3000);
      },
      error: (err: any) => {
        console.error('Error resetting password:', err);
        this.formLoading = false;
        
        // Clear success notification
        this.resetPasswordNotification = '';
        this.resetPasswordNotificationType = null;
        
        // Determine error message
        let errorMessage = '';
        if (err.status === 400 && err.error?.message) {
          const errorMsg = err.error.message.toLowerCase();
          if (errorMsg.includes('password') && (errorMsg.includes('required') || errorMsg.includes('length') || errorMsg.includes('6'))) {
            // Field-specific validation error - show below field
            this.passwordError = err.error.message;
            return; // Don't show notification for field-specific errors
          } else {
            errorMessage = err.error.message;
          }
        } else if (err.status === 0) {
          errorMessage = 'Cannot connect to server. Please make sure the backend API is running on http://localhost:4000';
        } else if (err.status === 401) {
          errorMessage = 'Unauthorized. Please login again.';
        } else if (err.status === 403) {
          errorMessage = 'Access denied. Only Admin can reset passwords.';
        } else if (err.status === 404) {
          errorMessage = 'Team member not found. Please refresh the page and try again.';
        } else if (err.status === 500) {
          errorMessage = err.error?.message || 'Server error. Please try again later.';
        } else {
          errorMessage = err.error?.message || err.message || 'Error resetting password. Please try again.';
        }
        
        // Show error notification (modal stays open)
        this.resetPasswordNotification = errorMessage;
        this.resetPasswordNotificationType = 'error';
        this.error = errorMessage;
        
        // Clear notification after 3 seconds
        setTimeout(() => {
          this.resetPasswordNotification = '';
          this.resetPasswordNotificationType = null;
        }, 3000);
      }
    });
  }

  closeSuccessModal() {
    this.showSuccessModal = false;
    this.successModalMessage = '';
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }
}

